import type { ClientOptions, StackFrame } from "@sentry/types";
import { getFramesFromEvent } from "@sentry/utils";

/**
 * URL schemes used by mobile in-app browsers (Facebook/Instagram on Android use `iabjs://`)
 * to inject their own instrumentation scripts (e.g. `navigation_performance_logger_android`).
 *
 * These scripts call back into native code via JavascriptInterface bridges. When the user
 * navigates and the WebView tears down the bridge, queued callbacks throw errors like
 * "Error invoking postMessage: Java object is gone" — which are unactionable for us.
 */
const IN_APP_BROWSER_SCHEMES = ["iabjs://"];

/**
 * Vendor-prefixed globals that mobile in-app browsers inject into the page so their
 * own bridge scripts can call back into native code. When the WebView tears down the
 * bridge (page navigation, app backgrounded, autofill closed, etc.) the queued
 * callback runs and the global is gone, surfacing as a `ReferenceError` via
 * `window.onerror`. The only stack frame is the HTML document itself, so the
 * scheme-based check above can't catch these.
 *
 * Each entry must be specific enough to never collide with a first-party identifier.
 */
const IN_APP_BROWSER_GLOBAL_PATTERNS: RegExp[] = [
  // Snapchat (`SCDynamicBridge`, also seen misspelled as `SCDynimacBridge` in the wild)
  /SCDyn\w*Bridge\b/,
  // Norton / Symantec Secure Browser
  /\bSymBrowser_\w+/,
  // Aloha Browser
  /\b__alhWeb\b/,
];

/**
 * `ReferenceError` message shapes across JS engines:
 *   - Safari / JavaScriptCore: "Can't find variable: NAME"
 *   - V8 / SpiderMonkey:       "NAME is not defined"
 */
const REFERENCE_ERROR_INDICATORS = ["Can't find variable:", "is not defined"];
const INVALID_ACCESS_ERROR_MESSAGE = "The object does not support the operation or argument.";
const IN_APP_BROWSER_BRIDGE_FUNCTION_NAMES = ["dispatchToBridge", "sendPostMessage"];

type ErrorDetails = {
  message?: string;
  name?: string;
};

const isInAppBrowserFrame = (frame: StackFrame): boolean =>
  IN_APP_BROWSER_SCHEMES.some(
    scheme => frame.filename?.startsWith(scheme) || frame.abs_path?.startsWith(scheme)
  );

const getErrorDetails = (originalException: unknown): ErrorDetails => {
  if (typeof originalException === "string") return { message: originalException };
  if (originalException instanceof Error) {
    return { message: originalException.message, name: originalException.name };
  }

  return {};
};

const isInAppBrowserBridgeReferenceError = (message: string | undefined): boolean => {
  if (!message) return false;
  if (!REFERENCE_ERROR_INDICATORS.some(indicator => message.includes(indicator))) return false;

  return IN_APP_BROWSER_GLOBAL_PATTERNS.some(pattern => pattern.test(message));
};

const isInvalidAccessError = ({ message, name }: ErrorDetails): boolean => {
  if (!message?.includes(INVALID_ACCESS_ERROR_MESSAGE)) return false;

  return name === "InvalidAccessError" || message.startsWith("InvalidAccessError:");
};

const isInAppBrowserBridgeInvalidAccessError = (
  errorDetails: ErrorDetails,
  frames: StackFrame[] | undefined
): boolean => {
  if (!isInvalidAccessError(errorDetails)) return false;

  return !!frames?.some(frame =>
    IN_APP_BROWSER_BRIDGE_FUNCTION_NAMES.some(functionName => frame.function === functionName)
  );
};

const inAppBrowserBridge: NonNullable<ClientOptions["beforeSend"]> = (event, hint) => {
  const errorDetails = getErrorDetails(hint?.originalException);
  const frames = getFramesFromEvent(event);

  if (isInAppBrowserBridgeReferenceError(errorDetails.message)) {
    return null;
  }

  if (isInAppBrowserBridgeInvalidAccessError(errorDetails, frames)) return null;

  if (frames?.some(isInAppBrowserFrame)) return null;

  return event;
};

export { inAppBrowserBridge };
